const CAP17 = {
  "id": 40,
  "title": "Las transacciones en Bitcoin",
  "slug": "las-transacciones-en-bitcoin",
  "type": "lesson",
  "category": "bitcoin",
  "level": "basic",
  "sections": [
    {
      "heading": "Introducción: ¿qué es una transacción en Bitcoin?",
      "text": [
        "Las transacciones, también conocidas como \"TX\", son el núcleo del funcionamiento de Bitcoin y de la mayoría de las criptomonedas. Son la pieza básica que permite mover valor dentro de la red y utilizar nuestros fondos de forma sencilla, rápida y segura.",
        "En una frase: una transacción es un envío o transferencia de valor entre dos partes. En Bitcoin, esas transferencias son registros que quedan guardados dentro de la blockchain: el libro mayor público y distribuido de la red.",
        "Técnicamente, una transacción de Bitcoin es un mensaje estructurado que contiene información concreta (entradas, salidas, firmas, comisiones…). Ese mensaje se firma criptográficamente y se transmite a todos los nodos para que la red lo valide y lo incluya en un bloque.",
        "Por eso se suele decir que Bitcoin es \"dinero programable\": las transacciones son datos que se pueden estructurar, automatizar y verificar mediante criptografía y reglas de consenso."
      ]
    },
    {
      "heading": "¿Cómo funcionan las transacciones en Bitcoin?",
      "text": [
        "Una transacción en Bitcoin ocurre cuando una persona envía BTC a otra mediante la red. Internamente, esta operación crea un registro que consume salidas anteriores (UTXO) y crea nuevas salidas asociadas a direcciones de destino.",
        "Este formato se usa también en otras criptomonedas como Litecoin y Bitcoin Cash.",
        "Para realizar una transacción es necesario usar un monedero (wallet), que administra las claves, el saldo y construye las transacciones."
      ]
    },
    {
      "heading": "¿Qué elementos conforman las transacciones?",
      "text": [
        "Toda transacción de Bitcoin está formada, como mínimo, por los siguientes componentes:"
      ],
      "list": [
        "Entradas (inputs): indican de dónde vienen los bitcoin que se están usando. Son referencias a salidas (UTXO) anteriores.",
        "Salidas (outputs): indican a qué direcciones se envían los bitcoins en la nueva transacción. Incluyen también la dirección de cambio.",
        "Identificador de transacción (TXid): hash único que identifica la transacción dentro de la blockchain.",
        "Tarifa de comisión (fee): pago a los mineros por procesar la transacción."
      ],
      "image": {
        "src": "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/Biucoinmaster/partes-de-una-tx-bit2me-explorer.jpg",
        "alt": "Ejemplo de transacción Bitcoin con hash, valores, entradas y salidas.",
        "caption": "Partes principales de una transacción real dentro de un explorador de bloques."
      }
    },
    {
      "heading": "Operativa interna: seguridad y scripts",
      "text": [
        "Todas las transacciones siguen una estructura general, pero su objetivo principal es garantizar la seguridad.",
        "Bitcoin usa un lenguaje llamado Bitcoin Script, que define las condiciones para gastar fondos.",
        "La verificación se realiza automáticamente usando criptografía asimétrica, sin necesidad de intermediarios."
      ]
    },
    {
      "heading": "Ejemplo del funcionamiento de una transacción en Bitcoin",
      "text": [
        "Imagina que Alicia tiene 1 BTC dividido en varias entradas (UTXO).",
        "Quiere enviar 0,3 BTC a Pedro. Su monedero agrupa tres entradas de 0,12 BTC cada una (0,36 BTC).",
        "Los 0,3 BTC van a Pedro y los 0,06 BTC restantes se envían a una dirección de cambio controlada por Alicia."
      ],
      "image": {
        "src": "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/Biucoinmaster/direcciones-bitcoin-retorno.jpg",
        "alt": "Diagrama con entradas agrupadas y salidas.",
        "caption": "Agrupación de entradas (UTXO) y generación de salidas: destino y cambio."
      }
    },
    {
      "heading": "Recompensas y comisiones para los mineros",
      "text": [
        "Los mineros reciben recompensas de dos fuentes:",
        "• La recompensa fija por bloque.\n• La suma de comisiones (fees) de todas las transacciones incluidas.",
        "Estas compensaciones se incluyen en una transacción especial llamada coinbase."
      ]
    },
    {
      "heading": "UTXO: salidas no gastadas",
      "text": [
        "El saldo real de un monedero está compuesto por UTXOs: salidas de transacciones anteriores que aún no han sido gastadas.",
        "Cada vez que se usa un UTXO, este se consume por completo y genera nuevas salidas (destino + cambio)."
      ]
    },
    {
      "heading": "Del par de claves a la transacción firmada",
      "text": [
        "Para enviar Bitcoin se necesitan dos elementos: dirección pública y clave privada.",
        "La dirección pública funciona como un número de cuenta. La clave privada permite firmar y autorizar transacciones.",
        "La clave privada nunca se comparte y es la que otorga la propiedad sobre los fondos."
      ],
      "image": {
        "src": "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/Biucoinmaster/generacion-bitcoin.jpg",
        "alt": "Proceso de generación de claves y dirección Bitcoin.",
        "caption": "Del texto aleatorio se genera la clave privada, la clave pública y finalmente la dirección Bitcoin."
      }
    },
    {
      "heading": "Enviar fracciones de bitcoin y límite dust",
      "text": [
        "Cada BTC se puede dividir en 100 millones de unidades llamadas satoshis (1 sat = 0,00000001 BTC).",
        "Sin embargo, existe un límite llamado dust, que evita enviar cantidades demasiado pequeñas para proteger la red."
      ]
    },
    {
      "heading": "Ventajas de las transacciones con Bitcoin",
      "list": [
        "Rapidez: confirmaciones en minutos, sin importar el país.",
        "Sin intermediarios: no requiere bancos ni procesadores.",
        "Irreversibilidad: una vez confirmada, no puede revertirse.",
        "Seguridad criptográfica: las claves privadas son imposibles de falsificar.",
        "Comisiones bajas: dependen del tamaño en bytes, no del monto enviado."
      ]
    },
     {
      "heading": "¿Quién registra las operaciones si no hay bancos?",
      "content": "Si no existe un banco que controle los saldos o los envíos de dinero, ¿quién se ocupa de registrar las operaciones? Aquí es donde entra en juego la famosa minería de Bitcoin. Los mineros son personas que configuran sus dispositivos para que trabajen verificando transacciones."
    },
    {
      "heading": "Recompensa por la verificación",
      "content": "A cambio de este esfuerzo, el sistema les entrega bitcoin que aún no están en circulación. Esta cantidad que reciben está destinada a cubrir los costes eléctricos de la tarea realizada a favor de la red."
    },
    {
      "heading": "Seguridad y crecimiento de la red",
      "content": "Al haber siempre muchas personas realizando esta actividad, el servicio para el uso de Bitcoin está totalmente garantizado. Esto ha hecho que su uso no pare de aumentar durante los más de diez años que lleva operativo, convirtiéndose así en la primera red de transacciones gobernada por los usuarios y, además, en la más segura y potente del mundo."
    },
    {
      "heading": "¿Cómo se hace la minería de Bitcoin?",
      "content": "En la blockchain de Bitcoin, la validación de las operaciones se realiza a través de unos nodos (ordenadores o dispositivos) conectados a la red, conocidos como mineros."
    },
    {
      "heading": "Rol de los mineros",
      "content": "Los mineros son los encargados de generar los nuevos bloques añadiéndolos al final de la cadena. Y como establece el protocolo Bitcoin, deben efectuar dicho proceso cada 10 minutos aproximadamente."
    },
    {
      "heading": "Contenido de los bloques",
      "content": "Cada bloque minado contiene un registro detallado de las diferentes transacciones realizadas en cada periodo. Y que tras ser añadidos al final de la blockchain actualizan su registro."
    },
    {
      "heading": "Proceso de minado",
      "content": "Para crear un nuevo bloque, los mineros deben realizar un complejo proceso para resolver un problema matemático. Al encontrar una solución válida para la red, entonces se entiende por 'minado' un nuevo bloque que será añadido a la blockchain en consenso."
    },
    {
      "heading": "Recompensa",
      "content": "El minero que encuentra la solución, recibirá una recompensa por el nuevo bloque."
    }
  ]
}


export default CAP17;
